// @ts-nocheck
import React from 'react';
import PropTypes from 'react-proptypes';
import cx from 'classnames';
import _ from 'lodash';
import async from 'async';
import qs from 'qs';
import {
  CustomValidationError,
  DateUtils,
  DefaultValues,
  Feature,
  FeatureValidator,
  FormValues,
  MediaElement,
  Record,
  RepeatableItemValue,
  RepeatableValue,
  SectionElement,
} from '@fulcrumapp/fulcrum-core';
import FeatureEditorMapView from '../data-view/map/feature-editor-map-view';
import RepeatableMapView from '../data-view/map/repeatable-map-view';
import PathParser from '../utils/path-parser';
import Dialog from '../shared/dialog';
import MessageBox from '../shared/message-box';
import AlertModal from '../components/modals/alert-modal';
import DataViewSource from '../data-view/models/data-view-source';
import ReportTemplatePicker from '../data-view/report-template-picker';
import { FormAppContext } from '../hooks/use-form-app';
import LocalStoragePreferences from '../utils/local-storage-preferences';
import type { Instruction } from '../hooks/use-form-fields';
import type { Coordinate, GeometricShape } from '../types/geo';
import DataFetcher from '../sketch-editor/data-fetcher';
import NavigationController from './views/navigation-controller';
import HeaderView from './views/header-view';
import NavigationToolbar from './views/navigation-toolbar';
import RecordEditorView from './views/record-editor-view';
import RepeatableListView from './views/repeatable-list-view';
import ExpressionEngine from './expressions/expression-engine';
import EditorLevel from './models/editor-level';
import { resolveMediaAddEvent, resolveMediaRemoveEvent } from './fields/media-type-config';
import EditorModal from './editor-modal';
import FeatureEditState from './models/feature-edit-state';
import hooks from './models/hooks';
import Extension from './views/extension';
import Colorizer from '../sketch-editor/colorizer';

// Only update calculation fields a maximum of every 50ms. This slightly delays the
// evaluation of the calculation fields when called in very quick succession. When
// typing into a textbox or when successive chained data events scripts produce results
// that cause many calculation fields to be updated.
const CALCULATION_FIELD_DELAY = 50;

export const DEFAULT_SAVE_ERROR_MESSAGE = 'There was a problem with saving your record. Please review and try again.';

const localStorage = new LocalStoragePreferences();

interface Props {
  addMode: boolean;
  previewMode: boolean;
  className?: string;
  editing?: boolean;
  topBorder?: boolean;
  path?: string;
  record: Record;
  feature?: Feature;
  parentValues?: FormValues;
  parentGeometry?: object;
  onClose?: Function;
  onSave?: Function;
  onDelete?: Function;
  onChildEnterEditMode?: Function;
  onChildRepeatableOpen?: Function;
  onChildRepeatableClose?: Function;
  onChildLinkedRecordOpen?: Function;
  onChildLinkedRecordClose?: Function;
  onLinkedRecordSave?: Function;
  onLinkedRecordDelete?: Function;
  modal: boolean;
  viewOnlyMode: boolean;
  expressionEngine?: ExpressionEngine;
  specialInstructions?: Instruction[];
}

export default class Editor extends React.Component<Props> {
  static propTypes = {
    addMode: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
    showValidationErrors: PropTypes.bool,
    repeatableValue: PropTypes.instanceOf(RepeatableValue),
    onFeatureSelect: PropTypes.func,
    previewMode: PropTypes.bool.isRequired,
    className: PropTypes.string,
    source: PropTypes.instanceOf(DataViewSource),
    editing: PropTypes.bool,
    isRepeatableLevel: PropTypes.bool,
    topBorder: PropTypes.bool,
    path: PropTypes.string,
    record: PropTypes.instanceOf(Record).isRequired,
    feature: PropTypes.instanceOf(Feature),
    parentValues: PropTypes.instanceOf(FormValues),
    parentGeometry: PropTypes.object,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    onChildEnterEditMode: PropTypes.func,
    onChildRepeatableOpen: PropTypes.func,
    onChildRepeatableClose: PropTypes.func,
    onChildLinkedRecordOpen: PropTypes.func,
    onChildLinkedRecordClose: PropTypes.func,
    modal: PropTypes.bool.isRequired,
    viewOnlyMode: PropTypes.bool.isRequired,
    expressionEngine: PropTypes.instanceOf(ExpressionEngine),
    specialInstructions: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        changes: PropTypes.arrayOf(PropTypes.shape({
          attributeName: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        })),
      }),
    ),
  };

  expressionEngine: ExpressionEngine;

  static contextType = FormAppContext;

  static defaultProps = {
    addMode: false,
    previewMode: false,
    topBorder: true,
    modal: true,
    viewOnlyMode: false,
  };

  constructor(props, context) {
    super(props, context);
    const editing = props.addMode || !!this.props.editing;

    context.layerSettings.on('change', () => this.setState({ layersLastUpdated: Date.now() }));

    const { levels } = this.createLevelsFromPath(props.path);

    const originalFeatureState = new FeatureEditState(levels[levels.length - 1].feature);

    this.applyDefaultValuesIfNecessary(editing);

    this.assignRecordIfNecessary();

    this.state = {
      editing,
      loading: false,
      addMode: props.addMode || false,
      levels,
      parentValues: null,
      mapVisible: this.context.preferences.isSectionExpanded('map') && levels[0].isMapEnabled,
      index: 0,
      showAlert: false,
      alertMessages: [],
      showMessageBox: false,
      messageBoxes: [],
      progressMessage: null,
      showProgress: false,
      showTemplatePicker: false,
      recordToPrint: null,
      showValidationErrors: false,
      validationErrorMessage: null,
      showDiscardChanges: false,
      showDeleteConfirmation: false,
      showUploadInProgressAlert: false,
      originalFeatureState,
      childFeature: null,
      linkedRecord: null,
      recordLinkValue: null,
      recordLinkItemValue: null,
      showExtension: false,
      extensionParams: null,
      highlightedFeature: null,
      hoveredFeature: null,
      matchesMd: window.matchMedia('(max-width: 1400px)').matches,
      matchesSm: window.matchMedia('(max-width: 960px)').matches,
      isOpenRecord: window.location.pathname.includes('records'),
      sidenavClosed: localStorage.get('sidenavClosed'),
      layersLastUpdated: Date.now(),
      repeatableMapFullRender: false,
    };

    // reset the field-level overrides if it's a new root editor
    if (this.currentLevel.isRecordLevel) {
      this.props.record.form.resetOverrides();
    }

    if (editing) {
      this.resetEditStartTime();
    }

    if (this.props.expressionEngine) {
      this.expressionEngine = this.props.expressionEngine;
    } else {
      this.expressionEngine = ExpressionEngine.acquire(this, this.context.globals.config.sandbox_url);
    }
  }

  UNSAFE_componentWillMount() {
    // TODO(zhm) verify the delegate is being set properly after backing out
    this.lastExpressionEngineDelegate = this.expressionEngine.delegate;
    this.expressionEngine.delegate = this;
    window.removeEventListener('resize', this.handleResize);
  }

  componentDidMount() {
    this.updateDimensions();
    window.addEventListener('resize', this.handleResize, false);

    this._isMounted = true;

    this.triggerOnLoad();

    this.updateCalculatedFields();

    window.__currentRecordEditor = this;

    hooks.emit('load-record-editor', this);

    // for media queries
    const handlerMd = (e) => this.setState({ matchesMd: e.matches });
    window.matchMedia('(max-width: 1400px)').addEventListener('change', handlerMd);

    const handlerSm = (e) => this.setState({ matchesSm: e.matches });
    window.matchMedia('(max-width: 960px)').addEventListener('change', handlerSm);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.shouldTriggerOnLoad(prevState)) {
      this.triggerOnLoad();
    }
  }

  shouldTriggerOnLoad(prevState) {
    const levelsChanged = prevState.levels !== this.state.levels;
    const startedEditing = !prevState.editing && this.state.editing;

    return levelsChanged || startedEditing;
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);

    this.expressionEngine.delegate = this.lastExpressionEngineDelegate;

    // if closing the root editor, dispose of the engine
    if (this.currentLevel.isRecordLevel) {
      this.expressionEngine.dispose();
    }

    this.expressionEngine = null;

    this._isMounted = false;

    hooks.emit('unload-record-editor', this);

    window.__currentRecordEditor = null;

    // for media queries
    window.matchMedia('(max-width: 1400px)').removeEventListener('change', this.handlerMd);
    window.matchMedia('(max-width: 960px)').removeEventListener('change', this.handlerSm);
  }

  render() {
    const { styles } = Editor;
    const { mapVisible, levels, sidenavClosed } = this.state;
    const levelDepth = levels.length - 1;
    const { views } = this.createViewsFromLevels(this.state.levels, this.state.editing);

    // conditional styling for .records.show & .records.new full screen layouts
    const { body } = document;
    const { isRepeatableLevel } = this.currentLevel;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const hasRecordsClass = body.classList.contains('records');
    const hasShowClass = body.classList.contains('show');
    const hasNewClass = body.classList.contains('new');
    const isFullScreenAndRepeatable = hasRecordsClass && isRepeatableLevel;
    const isFullScreenInShowMode = hasRecordsClass && hasShowClass;
    const isFullScreenInNewMode = hasRecordsClass && hasNewClass;

    function isFullScreenRepeatableOrAndSidenavClosed() {
      return isFullScreenAndRepeatable || (isFullScreenAndRepeatable && sidenavClosed);
    }

    function isFullScreenNewOrShowAndSidenavClosed() {
      return (isFullScreenInShowMode && sidenavClosed) || (isFullScreenInNewMode && sidenavClosed);
    }

    function isFullScreenNewOrShowAndSidenavOpen() {
      return (isFullScreenInShowMode || isFullScreenInNewMode);
    }

    const props = {
      className: styles.controller,
      views,
      mapVisible,
      viewWidth: (() => {
        switch (true) {
          case isFullScreenRepeatableOrAndSidenavClosed():
            return (viewportWidth - 48);
          case isFullScreenNewOrShowAndSidenavClosed():
            return (viewportWidth);
          case isFullScreenNewOrShowAndSidenavOpen():
            return (viewportWidth - 225);
          case (mapVisible && this.state.matchesMd):
            return 560;
          default:
            return 700;
        }
      })(),
    };

    // styling for media queries
    const style = {
      left: (() => {
        switch (true) {
          case isFullScreenAndRepeatable:
            return 24;
          case isFullScreenInShowMode || isFullScreenInNewMode:
            return 0;
          case !mapVisible || (mapVisible && this.state.matchesSm):
            return 'calc(50% - 350px)';
          default:
            return 24;
        }
      })(),
      width: (() => {
        switch (true) {
          case isFullScreenAndRepeatable:
            return 'auto';
          case isFullScreenInShowMode || isFullScreenInNewMode:
            return '100%';
          case mapVisible && this.state.matchesSm:
            return 700;
          case mapVisible:
            return 'auto';
          default:
            return 700;
        }
      })(),
      overflow: 'hidden',
    };
    const mapContainerStyle = {
      width: (() => {
        switch (true) {
          case mapVisible && this.state.matchesSm:
            return 700;
          case mapVisible:
            return 'auto';
          default:
            return 0;
        }
      })(),
      flex: mapVisible ? '1 1 0%' : 0,
      overflow: mapVisible ? 'unset' : 'hidden',
      minHeight: (mapVisible && this.state.matchesSm) && 300,
    };
    const headerStyle = {
      width: (() => {
        switch (true) {
          case mapVisible && this.state.matchesSm:
            return 700;
          case mapVisible && this.state.matchesMd:
            return 560;
          case mapVisible:
            return 700;
          default:
            return '100%';
        }
      })(),
    };
    const navigationContainerStyle = {
      width: (() => {
        switch (true) {
          case mapVisible && this.state.matchesSm:
            return 700;
          case mapVisible && this.state.matchesMd:
            return 560;
          case mapVisible:
            return 700;
          default:
            return '100%';
        }
      })(),
      position: (mapVisible && this.state.matchesSm) && 'relative',
      height: (mapVisible && this.state.matchesSm) && '60%',
    };
    const contentContainerStyle = {
      flexDirection: (mapVisible && this.state.matchesSm) ? 'column' : 'row',
    };
    // END styling for media queries

    const {
      className,
      feature,
      modal,
      parentGeometry,
      previewMode,
      record,
      source,
      topBorder,
      viewOnlyMode,
    } = this.props;
    const geometryTyped = isRepeatableLevel ? this.currentLevel.feature._element : this.currentLevel.feature._form;

    if ((this.state.childFeature || this.state.recordLinkValue) && modal) {
      style.transition = 'opacity 600ms ease, transform 600ms ease';
      const x = (-100 * this.state.index);
      const scale = 1 - (0.1 * this.state.index);
      style.transform = `translate(${x}px, 0) scale(${scale}, ${scale})`;
    }

    const statusColor = Colorizer(this.currentLevel.record.form).getColor(this.currentLevel.record.status);

    return (
      <div
        className={cx('editor-container', styles.container, className)}
        tabIndex="1"
        ref={this.bindContainer}
        style={style}
        data-testid={`editor-${levelDepth}`}
      >
        <HeaderView
          levelDepth={levelDepth}
          level={this.state.levels[this.state.levels.length - 1]}
          record={this.currentLevel.record}
          previewMode={previewMode}
          topBorder={topBorder}
          editing={this.state.editing}
          viewOnlyMode={viewOnlyMode || !this.context.role.canUpdateRecords}
          loading={this.state.loading}
          showCloseButton={modal}
          onAddClick={this.handleRepeatableListAddClick}
          onCloseClick={this.handleCloseClick}
          onEditClick={this.handleEditClick}
          onSaveClick={this.handleSaveClick}
          className={styles.header}
          style={headerStyle}
        />
        <div className={styles.contentContainer} style={contentContainerStyle}>
          <div className={styles.mapContainer} style={mapContainerStyle}>
            {this.currentLevel.repeatableValue == null ? (
              <FeatureEditorMapView
                addMode={this.state.addMode}
                editing={this.state.editing}
                form={record.form}
                visible={(this.currentLevel.isRecordLevel) && this.state.mapVisible}
                loading={this.currentLevel.loading}
                showValidationErrors={this.currentLevel.showValidationErrors}
                className={styles.map}
                status={this.currentLevel.feature.status || this.currentLevel.record.status}
                editable={this.state.editing && this.currentLevel.record.form.isManualLocationEnabled}
                latitude={this.currentLevel.feature.latitude}
                longitude={this.currentLevel.feature.longitude}
                geometry={this.currentLevel.feature.geometry}
                onGeometryChange={this.handleGeometryChange}
                fetchNearbyRecords={this.fetchNearbyRecords}
                geometryTyped={geometryTyped}
                parentView={isRepeatableLevel ? 'repeatable-editor' : 'record-editor'}
              />
            ) : (
              <RepeatableMapView
                className={styles.map}
                levelId={this.currentLevel.id}
                visible={this.shouldMapBeVisible}
                editable={this.state.editing}
                feature={this.currentLevel.feature}
                record={this.currentLevel.record}
                parentGeometry={parentGeometry}
                repeatableValue={this.currentLevel.repeatableValue}
                onFeatureSelect={this.handleRepeatableItemOpen}
                highlightedListFeature={this.state.highlightedFeature}
                statusColor={statusColor}
                getHighlightedFeature={() => this.state.highlightedFeature}
                setHoveredFeature={this.handleRepeatableItemHighlight}
                fetchNearbyRecords={this.fetchNearbyRecords}
                repeatableMapFullRender={this.handleRepeatableMapFullRender}
              />
            )}
          </div>
          <div className={styles.navigationContainer} style={navigationContainerStyle}>
            <NavigationToolbar
              source={source}
              levels={this.state.levels}
              viewOnlyMode={viewOnlyMode}
              onCrumbClick={this.handleCrumbClick}
              onBackClick={this.handleBackClick}
              record={record}
              feature={feature}
              mapVisible={this.state.mapVisible}
              onMapClick={this.handleMapClick}
              onPrintClick={this.handlePrintClick}
              onHistoryClick={this.handleHistoryClick}
              onDeleteClick={this.handleDeleteClick}
              className={styles.navigationBar}
            />
            <NavigationController
              ref={this.bindNavigationController}
              {...props}
            />
          </div>
        </div>
        {this.renderChildEditor()}
        {this.renderLinkedRecordEditor()}
        {this.renderExtension()}

        <ReportTemplatePicker
          show={this.state.showTemplatePicker}
          form={this.currentLevel.record.form}
          onDone={this.handleTemplatePickerDone}
          onCancel={this.handleTemplatePickerCancel}
        />
        <Dialog
          showTop
          negative={null}
          show={this.state.showValidationErrors}
          onPositiveClick={this.handleValidationPositiveClick}
        >
          <div>{this.state.validationErrorMessage}</div>
        </Dialog>
        <Dialog
          negative={null}
          show={this.state.showAlert}
          title={this.state.alertMessages[0]?.title}
          onPositiveClick={this.handleAlertPositiveClick}
        >
          <div>{this.state.alertMessages[0]?.message}</div>
        </Dialog>
        {this.renderMessageBox()}
        <Dialog
          negative={null}
          positive={null}
          show={this.state.showProgress}
        >
          <div>{this.state.progressMessage}</div>
        </Dialog>
        <Dialog
          negative="Cancel"
          positive="Yes, Discard"
          title="This record has not been saved."
          show={this.state.showDiscardChanges}
          onNegativeClick={this.handleDiscardCancelClick}
          onPositiveClick={this.handleDiscardOkayClick}
        >
          <div>
            <p>Are you sure you want to discard any changes?</p>
          </div>
        </Dialog>
        <Dialog
          negative="Cancel"
          positive="Yes, Delete"
          title="Confirmation"
          show={this.state.showDeleteConfirmation}
          onNegativeClick={this.handleDeleteCancelClick}
          onPositiveClick={this.handleDeleteOkayClick}
        >
          {this.renderDeleteConfirmationContent()}
        </Dialog>
        <AlertModal
          open={this.state.showUploadInProgressAlert}
          title="Upload in Progress"
          message="Please wait for the files to finish uploading."
          onClose={() => this.setState({ showUploadInProgressAlert: false })}
        />
      </div>
    );
  }

  handleMapFeatureSelect = (item) => {
    this.openItem(item);
  };

  renderDeleteConfirmationContent() {
    if (!this.state.showDeleteConfirmation) {
      return null;
    }

    const destructiveWarnings = [];
    const childRecords = this.currentLevel.record.formValues.repeatableItems.length;

    if (childRecords) {
      destructiveWarnings.push(childRecords === 1 ? '1 child record' : `${childRecords} child records`);
    }

    return (
      <div>
        <p>Are you sure you want to delete this record?</p>
        {destructiveWarnings.length > 0
        && (
          <div>
            The following associated objects will also be deleted:
            <ul>
              <li>
                {destructiveWarnings}
              </li>
            </ul>
          </div>
        )}
        <p>This action cannot be undone.</p>
      </div>
    );
  }

  renderChildEditor() {
    const { mapVisible } = this.state;
    const { source } = this.props;

    return (
      <EditorModal
        show={this.state.childFeature != null}
        previewMode={this.props.previewMode}
        record={this.props.record}
        feature={this.state.childFeature}
        mapVisible={mapVisible}
        parentGeometry={this.currentLevel.feature?.geometry}
        parentValues={this.currentLevel.parentValues}
        viewOnlyMode={this.props.viewOnlyMode}
        addMode={this.state.childFeature ? !this.state.childFeature.createdAt : false}
        path=""
        expressionEngine={this.expressionEngine}
        onClose={this.handleChildRepeatableEditorClose}
        onSave={this.handleRepeatableSave}
        onChildEnterEditMode={this.handleChildEnterEditMode}
        onChildRepeatableClose={this.handleChildRepeatableEditorClose}
        onChildRepeatableOpen={this.handleChildRepeatableEditorOpen}
        onLinkedRecordSave={this.props.onLinkedRecordSave}
        onLinkedRecordDelete={this.props.onLinkedRecordDelete}
        source={source}
      />
    );
  }

  renderLinkedRecordEditor() {
    const { source } = this.props;

    let allowUpdating = this.state?.recordLinkValue?.element?.allowUpdating;

    if (this.props.viewOnlyMode) {
      allowUpdating = false;
    }
    const { mapVisible } = this.state;

    return (
      <EditorModal
        show={this.state.recordLinkValue != null}
        previewMode={this.props.previewMode}
        record={this.state.linkedRecord}
        feature={this.state.linkedRecord}
        mapVisible={mapVisible}
        viewOnlyMode={!allowUpdating}
        parentValues={null}
        addMode={this.state.linkedRecord ? !this.state.linkedRecord.createdAt : false}
        path=""
        onClose={this.handleChildLinkedRecordEditorClose}
        onSave={this.handleLinkedRecordSave}
        onDelete={this.handleLinkedRecordDelete}
        onChildLinkedRecordClose={this.handleChildLinkedRecordEditorClose}
        onChildLinkedRecordOpen={this.handleChildLinkedRecordEditorOpen}
        onLinkedRecordSave={this.props.onLinkedRecordSave}
        onLinkedRecordDelete={this.props.onLinkedRecordDelete}
        source={source}
      />
    );
  }

  renderExtension() {
    const { extensionParams, showExtension } = this.state;
    return (
      <Extension
        formId={this.currentLevel.record.form.id}
        visible={showExtension}
        id={extensionParams?.id}
        url={extensionParams?.url}
        width={extensionParams?.width}
        height={extensionParams?.height}
        title={extensionParams?.title}
        data={extensionParams?.data}
        onHide={this.handleExtensionHidden}
      />
    );
  }

  renderMessageBox() {
    const messageBox = this.state.messageBoxes.length ? this.state.messageBoxes[0] : null;

    return (
      <MessageBox
        buttons={messageBox?.buttons}
        title={messageBox?.title}
        message={messageBox?.message}
        input={messageBox?.input}
        placeholder={messageBox?.placeholder}
        default={messageBox?.default}
        show={this.state.showMessageBox}
        onDismiss={this.handleMessageBoxDismiss}
      />
    );
  }

  bindNavigationController = (node) => {
    this.navigationController = node;
  };

  bindContainer = (node) => {
    this.container = node;
  };

  handleDiscardCancelClick = (event) => {
    this.setState({ showDiscardChanges: false });
  };

  handleDiscardOkayClick = (event) => {
    const { closing } = this.state;

    this.setState({ showDiscardChanges: false });

    if (closing) {
      this.close();
    } else {
      this.cancel();
    }
  };

  handleDeleteCancelClick = (event) => {
    this.setState({ showDeleteConfirmation: false });
  };

  handleDeleteOkayClick = (event) => {
    this.setState({ showDeleteConfirmation: false });

    this.context.dataSource.deleteRecord(this.currentLevel.record, (err, record) => {
      if (err) {
        throw err;
      }

      if (this.props.onDelete) {
        this.props.onDelete(record);
      }

      this.close();
    });
  };

  handleRepeatableItemRemove = (item) => {
    this.updateParentValues();

    this.setFormValue(this.currentLevel.repeatableValue.element, this.currentLevel.repeatableValue, null, () => {
      this.updateView();
    });
  };

  handleRepeatableSave = (item) => {
    this.currentLevel.repeatableValue.insertItem(item);

    this.updateParentValues();

    this.setFormValue(this.currentLevel.repeatableValue.element, this.currentLevel.repeatableValue, null, () => {
      this.updateView();
    });
  };

  handleRepeatableItemHighlight = (item) => {
    this.setState((prevState) => ({ ...prevState, highlightedFeature: item }));
  };

  updateParentValues() {
    // Update the parent values to reflect the latest changes. This allows expressions and data events
    // to work when referencing "themselves", e.g. referencing sibling items within the current repeatable.
    if (this.currentLevel.parentValues) {
      this.currentLevel.parentValues.set(this.currentLevel.repeatableValue.element.key,
        this.currentLevel.repeatableValue);
    }
  }

  handleLinkedRecordSave = (record) => {
    if (this.state.editing) {
      const { recordLinkValue } = this.state;

      this.setFormValue(recordLinkValue.element, recordLinkValue, null, () => {
        this.updateRecordLinkDefaults(recordLinkValue, record);
      });
    }

    // if there is anything using the titles pointing to this record, invalidate their titles
    if (this.props.onLinkedRecordSave) {
      this.props.onLinkedRecordSave(record);
    }
  };

  handleLinkedRecordDelete = (record) => {
    if (this.state.recordLinkItemValue) {
      this.state.recordLinkItemValue._record = null;
    }

    // if there is anything using the titles pointing to this record, invalidate their titles
    if (this.props.onLinkedRecordDelete) {
      this.props.onLinkedRecordDelete(record);
    }
  };

  handleAlertPositiveClick = () => {
    this.state.alertMessages.shift();
    this.setState({ showAlert: this.state.alertMessages.length > 0, alertMessages: this.state.alertMessages.slice(0) });
  };

  handleMessageBoxDismiss = (index, input) => {
    const messageBox = this.state.messageBoxes.shift();

    this.setState({ showMessageBox: this.state.messageBoxes.length > 0, messageBoxes: this.state.messageBoxes.slice(0) });

    const result = {
      index,
      input: input ?? null,
      value: index >= 0 ? messageBox.buttons[index] : null,
    };

    messageBox.callback(result);
  };

  handleValidationPositiveClick = () => {
    this.setState({ showValidationErrors: false });
  };

  handleResize = (event) => {
    if (this._frame) {
      return;
    }

    this._frame = window.requestAnimationFrame(() => {
      this._frame = null;
      this.updateDimensions();
    });
  };

  handleFocusChange = (fieldView, focused) => {
    if (fieldView.props.element.isTextElement) {
      this.triggerEvent(focused ? 'focus' : 'blur', null, fieldView.props.element.dataName);

      this.updateCalculatedFields();
    }
  };

  handleHyperlinkClick = (formValue) => {
    this.triggerEvent('click', null, formValue.element.dataName);

    this.updateCalculatedFields();
  };

  handleStatusChange = (status) => {
    this.props.record.status = status;

    this.triggerEvent('change-status', status, null);

    this.updateCalculatedFields();
  };

  handleAssignmentChange = (user) => {
    this.props.record.assignedTo = user;

    this.triggerEvent('change-assignment', user ? user.email : null, null);

    this.updateCalculatedFields();
  };

  handleProjectChange = (project) => {
    this.props.record.project = project;

    this.triggerEvent('change-project', project ? project.name : null, null);

    this.updateCalculatedFields();
  };

  handleGeometryChange = (geometry: Coordinate | GeometricShape | null) => {
    this.updateGeometry(geometry);

    if (this.currentLevel.feature instanceof Record) {
      this.currentLevel.feature.horizontalAccuracy = null;
      this.currentLevel.feature.verticalAccuracy = null;
      this.currentLevel.feature.speed = null;
      this.currentLevel.feature.course = null;
    }

    const dataName = this.isRepeatableEditor ? this.currentLevel.feature.element.dataName
      : null;

    this.triggerEvent('change-geometry',
      this.currentLevel.feature.geometryAsGeoJSON,
      dataName);

    this.updateCalculatedFields();
  };

  handleFieldChange = (formValue, value) => {
    this.setFormValue(formValue.element, formValue, null, () => {
      this.updateView();
    });
  };

  handleMediaAdd = (mediaValue, metadata, callback) => {
    const eventName = resolveMediaAddEvent(mediaValue.element);

    const completion = (errors) => {
      if (errors.length) {
        this.setState({
          showValidationErrors: true,
          validationErrorMessage: FeatureValidator.formatErrors(errors),
        });
      } else {
        /* eslint-disable callback-return */
        callback();
        /* eslint-enable callback-return */
      }

      this.updateView();
    };

    if (eventName) {
      this.triggerEvent(eventName,
        metadata,
        mediaValue.element.dataName,
        completion);
    }
  };

  handleMediaRemove = (mediaValue, metadata) => {
    const eventName = resolveMediaRemoveEvent(mediaValue.element);

    if (eventName) {
      this.triggerEvent(eventName,
        metadata,
        mediaValue.element.dataName);
    }
  };

  handleCloseClick = () => {
    // https://gist.github.com/zhm/3aeb1148a6bdc3a6bfc6d90f444e2aa9

    if (this.currentLevel.isRepeatableIndexLevel || this.currentLevel.isSectionLevel) {
      // if the close button is clicked from an repeatable index or drilldown, just go back
      this.handleBackClick();
    } else if (this.state.editing) {
      if (this.state.addMode) {
        if (this.hasChanges && !this.props.previewMode) {
          // if currently editing *in add mode* and there are changes, prompt to discard and continue closing the editor
          this.setState({ showDiscardChanges: true, closing: true });
        } else {
          // if currently editing *in add mode* and there are no changes, just close the window
          this.close();
        }
      } else if (this.hasChanges && !this.props.previewMode) {
        // if currently editing an existing record and there are changes, prompt to discard and continue closing the editor
        this.setState({ showDiscardChanges: true, closing: false });
      } else {
        // if currently editing an existing record and there are no changes, exit edit mode (go back to view mode)
        this.cancel();
      }
    } else {
      // if not currently in edit mode, just close the editor
      this.close();
    }
  };

  handleMapClick = () => {
    const mapVisible = !this.state.mapVisible;

    this.setState({ mapVisible });

    this.context.preferences.setSectionExpanded('map', mapVisible);
  };

  handlePrintClick = () => {
    const { record } = this.currentLevel;

    if (record.form.reportTemplates.length > 1) {
      this.setState({ showTemplatePicker: true, recordToPrint: record });
      return;
    }

    this.runReport(record, record.form.reportTemplate);
  };

  runReport = (record, template) => {
    const params = {
      report: {
        record_id: record.id,
      },
    };

    if (template) {
      params.report.template_id = template.id;
    }

    const url = `/reports/new?${qs.stringify(params)}`;

    window.open(url);
  };

  handleTemplatePickerDone = (template) => {
    this.runReport(this.state.recordToPrint, template);
    this.setState({ showTemplatePicker: false, recordToPrint: null });
  };

  handleTemplatePickerCancel = () => {
    this.setState({ showTemplatePicker: false, recordToPrint: null });
  };

  handleHistoryClick = () => {
    window.open(`/records/${this.currentLevel.record.id}/history`);
  };

  handleDeleteClick = () => {
    this.setState({ showDeleteConfirmation: true });
  };

  handleSectionNavigate = (element) => {
    const newLevel = new EditorLevel(this.currentLevel.record,
      element,
      this.currentLevel.feature,
      this.currentLevel.parentValues);

    this.state.levels.push(newLevel);

    this.setState({ levels: this.state.levels });
  };

  handleRepeatableNavigate = (repeatableValue) => {
    const newLevel = new EditorLevel(this.currentLevel.record,
      this.currentLevel.rootElement,
      this.currentLevel.feature,
      this.currentLevel.valuesForConditions,
      repeatableValue);

    this.state.levels.push(newLevel);

    this.setState({ levels: this.state.levels });
  };

  handleEditClick = () => {
    const nowEditing = !this.state.editing;

    this.resetEditStartTime();

    if (nowEditing) {
      this.applyDefaultValuesIfNecessary(nowEditing);
    }

    this.setState({ editing: nowEditing });

    if (nowEditing) {
      this.updateCalculatedFieldsNow();

      if (this.props.onChildEnterEditMode) {
        this.props.onChildEnterEditMode();
      }
    }
  };

  handleUploadingChange = (isUploading) => {
    this._isMediaUploading = isUploading;

    if (!isUploading && this.state.showUploadInProgressAlert) {
      this.setState({ showUploadInProgressAlert: false });
    }
  };

  handleSaveClick = () => {
    if (this._isMediaUploading) {
      this.setState({ showUploadInProgressAlert: true });
      return;
    }

    this.save({ validate: true, triggerSaveEvent: true });
  };

  handleFeatureChange = (feature) => {
    this.setState({ feature });
  };

  handleRepeatableListAddClick = () => {
    const { repeatableValue } = this.currentLevel;

    const item = repeatableValue.createNewItem();

    DefaultValues.applyDefaultValuesForElements(repeatableValue.element.elements,
      item.formValues,
      this.currentLevel.record);

    this.setState({ childFeature: item, index: this.state.index + 1 });

    if (this.props.onChildRepeatableOpen) {
      this.props.onChildRepeatableOpen();
    }
  };

  handleRepeatableItemOpen = (item) => {
    this.setState({ childFeature: item, index: this.state.index + 1 });

    if (this.props.onChildRepeatableOpen) {
      this.props.onChildRepeatableOpen();
    }
  };

  handleLinkedRecordNew = (recordLinkValue) => {
    const newRecord = recordLinkValue.element.form.createRecord();

    this.setState({
      linkedRecord: newRecord,
      recordLinkValue,
      recordLinkItemValue: null,
      index: this.state.index + 1,
    });

    if (this.props.onChildLinkedRecordOpen) {
      this.props.onChildLinkedRecordOpen();
    }
  };

  handleLinkedRecordOpen = (item) => {
    this.setState({
      linkedRecord: item.record,
      recordLinkValue: item.parent,
      recordLinkItemValue: item,
      index: this.state.index + 1,
    });

    if (this.props.onChildLinkedRecordOpen) {
      this.props.onChildLinkedRecordOpen();
    }
  };

  handleChildEnterEditMode = () => {
    this.setState({ editing: true });

    this.resetEditStartTime();

    if (this.props.onChildEnterEditMode) {
      this.props.onChildEnterEditMode();
    }
  };

  handleChildRepeatableEditorOpen = () => {
    this.setState({ index: this.state.index + 1 });

    if (this.props.onChildRepeatableOpen) {
      this.props.onChildRepeatableOpen();
    }
  };

  handleChildRepeatableEditorClose = () => {
    const state = {
      index: this.state.index - 1,
      repeatableMapFullRender: true,
    };

    if (state.index === 0) {
      state.childFeature = null;
    }

    this.setState(state);

    if (this.props.onChildRepeatableClose) {
      this.props.onChildRepeatableClose(this);
    }
  };

  handleRepeatableMapFullRender = () => {
    if (this.state.repeatableMapFullRender === true) {
      this.setState({ repeatableMapFullRender: false });
      return true;
    }
    return false;
  };

  handleChildLinkedRecordEditorOpen = () => {
    this.setState({ index: this.state.index + 1 });

    if (this.props.onChildLinkedRecordOpen) {
      this.props.onChildLinkedRecordOpen();
    }
  };

  handleChildLinkedRecordEditorClose = () => {
    const state = {
      index: this.state.index - 1,
    };

    if (state.index === 0) {
      // state.linkedRecord = null;
      state.recordLinkValue = null;
      state.recordLinkItemValue = null;
    }

    this.setState(state);

    if (this.props.onChildLinkedRecordClose) {
      this.props.onChildLinkedRecordClose(this);
    }
  };

  handleExtensionHidden = (source: string) => {
    this.onProcessExtensionMessage({
      close: true,
      source,
    });
  };

  handleBackClick = () => {
    if (this.state.levels.length > 1) {
      this.handleCrumbClick(this.state.levels[this.state.levels.length - 2]);
    }
  };

  handleCrumbClick = (item) => {
    // the last item in the crumbs is the current one, it doesn't do anything
    if (this.state.levels[this.state.levels.length - 1] === item) {
      return;
    }

    const newIndex = _.findIndex(this.state.levels, item);

    const newLevels = _.dropRight(this.state.levels,
      this.state.levels.length - newIndex - 1);

    // when going back, make sure to update any calc fields
    this.setState({ levels: newLevels }, () => {
      this.updateCalculatedFields();
    });
  };

  handleLinkedRecordSelect = (recordLinkValue, record) => {
    recordLinkValue.addRecord(record);

    this.setFormValue(recordLinkValue.element, recordLinkValue, null, () => {
      this.updateRecordLinkDefaults(recordLinkValue, record);
    });
  };

  // This is intentionally untested
  // If previewMode is active. Skipping fetchNearbyRecords
  fetchNearbyRecords = async (excludedIds?: string[]) => {
    const { feature, parentValues } = this.currentLevel;
    const {
      parentGeometry, previewMode, record, source,
    } = this.props;

    if (previewMode) {
      return [];
    }

    if (!source) {
      throw new Error(`DataView not found while trying to get nearby records for '${JSON.stringify(feature)}'`);
    }

    const dataFetcher = DataFetcher(feature, record.status, source);

    if (feature.isRepeatable) {
      return dataFetcher.fetchParentAndSiblings(parentValues, parentGeometry);
    }
    return dataFetcher.fetchNearbyRecords(excludedIds);
  };

  save = ({ validate, triggerSaveEvent }: { validate: boolean, triggerSaveEvent: boolean}) => {
    // https://gist.github.com/zhm/3aeb1148a6bdc3a6bfc6d90f444e2aa9

    if (this.currentLevel.isRepeatableIndexLevel || this.currentLevel.isSectionLevel) {
      // if the done/save button is clicked from an repeatable index, just go back
      this.handleBackClick();
    } else if (this.state.editing) {
      const afterValidate = (errors) => {
        if (errors.length) {
          this.setState({ showValidationErrors: true, validationErrorMessage: FeatureValidator.formatErrors(errors) });
        } else if (this.currentLevel.isRecordLevel && this.props.previewMode) {
          this.close();
        } else if (this.hasChanges || this.isEditingViaUrlAction) {
          this.saveFeature(triggerSaveEvent);
        } else {
          // if currently editing and there are no changes to be saved, just exit edit mode
          this.cancel();
        }
      };

      if (validate) {
        // if currently editing, validate the feature, and if there are changes, save the feature (which closes repeatables)
        this.validateFeature(afterValidate);
      } else {
        afterValidate([]);
      }
    } else {
      // when just viewing a record, just close the editor window
      this.close();
    }
  };

  saveRecord() {
    const completion = (record) => {
      const featureState = new FeatureEditState(record);

      this.setState({
        originalFeatureState: featureState,
        showValidationErrors: false,
        editing: false,
        loading: false,
        addMode: false,
      });

      if (this.props.onSave) {
        this.props.onSave(record);
      }
    };

    if (this.props.previewMode) {
      completion(this.currentLevel.record);
    } else {
      this.context.dataSource.saveRecord(this.currentLevel.record, (err, record) => {
        if (err) {
          this.handleAPIRecordError(err);
        } else {
          completion(record);
        }
      });
    }
  }

  handleAPIRecordError(err: Error) {
    // some 500 errors are returning webpages instead of API error messages
    // which is causing more errors we try to `parse`
    const isBodyValid = err.status !== 500 && typeof err.body === 'string' && !!err.body.trim().length;
    const body = isBodyValid ? JSON.parse(err.body) : null;
    let errorMessage = DEFAULT_SAVE_ERROR_MESSAGE;

    if (body) {
      errorMessage = this.formatAPIErrorMessage(body.record?.errors);
    }

    this.setState({
      showValidationErrors: true,
      validationErrorMessage: errorMessage,
      loading: false,
    });
  }

  formatAPIErrorMessage(errors) {
    let errorMessage: string = DEFAULT_SAVE_ERROR_MESSAGE;

    const geometryError = errors?.geometry;
    if (geometryError) {
      errorMessage = `Geometry ${geometryError[0]}`;
    }

    return errorMessage;
  }

  showMessageBox(options, callback) {
    const messageBox = {
      ...options,
      callback,
    };

    this.state.messageBoxes.push(messageBox);
    this.setState({ showMessageBox: true, messageBoxes: this.state.messageBoxes.slice(0) });
  }

  createLevelsFromPath(path) {
    const container = this.props.feature?.element ? this.props.feature.element
      : this.props.record?.form;

    const paths = PathParser.parse(this.props.record,
      this.props.feature ?? this.props.record,
      container,
      path);

    const { record } = this.props;

    let { parentValues } = this.props;
    let currentFeature = record;

    const levels = [];

    for (const level of paths) {
      if (level?.element?.isRepeatableElement && level.feature == null) {
        const repeatableValue = currentFeature.formValues.get(level.element.key);

        levels.push(new EditorLevel(record, level.element, currentFeature, parentValues, repeatableValue));
      } else if (level.element instanceof SectionElement) {
        levels.push(new EditorLevel(record, level.element, currentFeature, parentValues));
      } else if (level.feature instanceof RepeatableItemValue) {
        currentFeature = level.feature;

        levels.push(new EditorLevel(record, level.element, currentFeature, parentValues));
      } else if (level.feature instanceof Record) {
        levels.push(new EditorLevel(record, null, currentFeature, parentValues));
      } else {
        throw Error('invalid level');
      }

      const copy = currentFeature.formValues.copy();

      parentValues = parentValues ? copy.merge(parentValues) : copy;
    }

    return { levels };
  }

  createViewsFromLevels(levels, editing) {
    const views = [];

    for (const level of levels) {
      let view = null;

      if (level.isRepeatableIndexLevel) {
        view = this.createRepeatableListView(level, editing);
      } else {
        view = this.createEditorView(views.length, level, editing);
      }

      views.push(view);
    }

    return { views };
  }

  createRepeatableListView(level, editing) {
    const { mapVisible } = this.state;
    const { record } = this.currentLevel;
    const { parentGeometry } = this.props;

    return (
      <RepeatableListView
        levelId={level.id}
        editing={editing}
        record={record}
        feature={level.feature}
        parentGeometry={parentGeometry}
        repeatable={level.repeatableValue.element}
        repeatableValue={level.repeatableValue}
        parentValues={level.valuesForConditions}
        mapVisible={mapVisible}
        onGeometryChange={this.handleGeometryChange}
        onRepeatableItemOpen={this.handleRepeatableItemOpen}
        onRepeatableItemRemove={this.handleRepeatableItemRemove}
        onRepeatableItemHighlight={this.handleRepeatableItemHighlight}
        fetchNearbyRecords={this.fetchNearbyRecords}
        highlightedFeature={this.state.highlightedFeature}
      />
    );
  }

  createEditorView(key, level, editing) {
    const { specialInstructions, parentGeometry } = this.props;
    const {
      loading, addMode, mapVisible, showValidationErrors,
    } = this.state;

    return (
      <RecordEditorView
        key={key}
        level={level}
        addMode={addMode}
        mapVisible={!!(level.isRecordLevel || level.isRepeatableLevel) && !!mapVisible}
        editing={editing}
        loading={loading}
        showValidationErrors={showValidationErrors}
        onGeometryChange={this.handleGeometryChange}
        onFieldChange={this.handleFieldChange}
        onSectionNavigate={this.handleSectionNavigate}
        onLinkedRecordSelect={this.handleLinkedRecordSelect}
        onLinkedRecordNew={this.handleLinkedRecordNew}
        onLinkedRecordOpen={this.handleLinkedRecordOpen}
        onHyperlinkClick={this.handleHyperlinkClick}
        onFocusChange={this.handleFocusChange}
        onStatusChange={this.handleStatusChange}
        onProjectChange={this.handleProjectChange}
        onAssignmentChange={this.handleAssignmentChange}
        onMediaAdd={this.handleMediaAdd}
        onMediaRemove={this.handleMediaRemove}
        onRepeatableNavigate={this.handleRepeatableNavigate}
        onUploadingChange={this.handleUploadingChange}
        specialInstructions={specialInstructions}
        fetchNearbyRecords={this.fetchNearbyRecords}
        parentGeometry={parentGeometry}
      />
    );
  }

  cancel() {
    this.state.originalFeatureState.restore(this.currentLevel.feature);

    this.setState({ editing: !this.state.editing });

    this._defaultsApplied = false;

    if (this.currentLevel.isRecordLevel) {
      this.triggerEvent('cancel-record');
    }

    if (this.currentLevel.isRepeatableLevel) {
      this.triggerEvent('cancel-repeatable', null, this.currentLevel.feature.element.dataName);
    }
  }

  validateFeature(callback) {
    const errors = this.validationErrors;

    this.triggerOnValidate((customErrors) => {
      if (customErrors.length) {
        errors.push.apply(errors, customErrors);
      }

      callback(errors);
    });
  }

  updateView() {
    this.setState({ levels: this.state.levels });
  }

  updateViewPrettySoon() {
    // Delay reloading of the view because this might be called many times in quick succession
    // when calculation fields are changing while data is changing. Since we fire change events
    // recursively for calculation fields, we update things *a lot*. It's compounded by calculation
    // fields also firing `change` events for data events, which in turn can cause further changes
    // which require updating calculation fields.
    clearTimeout(this._updateViewTimeout);

    this._updateViewTimeout = setTimeout(() => {
      this._updateViewTimeout = null;
      this.setState({ levels: this.state.levels });
    }, CALCULATION_FIELD_DELAY);
  }

  triggerOnValidate(callback) {
    if (this.currentLevel.feature instanceof Record) {
      this.triggerEvent('validate-record', null, null, callback);
    } else if (this.currentLevel.feature instanceof RepeatableItemValue) {
      this.triggerEvent('validate-repeatable', null, this.currentLevel.feature.element.dataName, callback);
    }
  }

  triggerOnLoad() {
    if (this.isRecordEditor) {
      this.triggerEventOnce('load-record');

      if (this.state.editing) {
        if (this.state.addMode) {
          this.triggerEventOnce('new-record');
        } else {
          this.triggerEventOnce('edit-record');
        }
      }
    }

    if (this.isRepeatableEditor) {
      const { dataName } = this.currentLevel.feature.element;

      this.triggerEventOnce('load-repeatable', dataName);

      if (this.state.editing) {
        if (this.state.addMode) {
          this.triggerEventOnce('new-repeatable', dataName);
        } else {
          this.triggerEventOnce('edit-repeatable', dataName);
        }
      }
    }
  }

  triggerEventOnce(name, dataName) {
    this._events = this._events || {};

    if (this._events[name]) {
      return;
    }

    this._events[name] = new Date();

    this.triggerEvent(name, null, dataName);
  }

  triggerEvent(name, value, dataName, callback) {
    const event = {
      name,
      field: dataName || null,
      value: value || null,
    };

    this.triggerDataEvent(event, callback);
  }

  triggerValueChange(element, formValue, callback) {
    const event = {
      name: 'change',
      field: element.dataName,
      value: formValue.toJSON(),
    };

    this.triggerDataEvent(event, callback);
  }

  triggerDataEvent(event, cb) {
    const callback = cb || (() => this.updateView());

    if (this._eventQueue == null) {
      this._eventQueue = [];
    }

    const errors = this.validationErrors;

    event.errors = errors.map((error) => ({ field: error.element?.dataName, message: error.message }));
    event.isValid = errors.length === 0;
    event.isDraft = false; // only mobile supports draft records currently

    this._eventQueue.push({ event, callback });

    this.triggerNextDataEvent();
  }

  triggerNextDataEvent() {
    // Use a queue to sequence the data event processing. A single data event trigger consists
    // of a series of asynchronous operations that have to be ran in succession without other
    // data events running while it's being processed. A single data event trigger has to prepare,
    // set the values, and then execute and process the results as an atomic set of operations.
    // We can't being processing another data event in the middle of setting the values and executing
    // the trigger. When the form loads, we setup the engine and immediately call load-record,
    // edit-record, etc. We need to make sure the results are processed in the correct order. This
    // issue was discovered when using a SETREADONLY() call at the global scope within a data event.
    if (this._eventQueue.length === 0) {
      return;
    }

    if (this._processingDataEvent) {
      return;
    }

    this._processingDataEvent = true;

    const engine = this.expressionEngine;

    const item = this._eventQueue.shift();

    const handler = (results) => {
      this.processExpressionResults(results, (result) => {
        item.callback(result);

        this._processingDataEvent = false;

        this.triggerNextDataEvent();
      });
    };

    engine?.evaluateDataEvent(this.currentLevel.record.form,
      this.currentLevel.valuesForConditions,
      this.getFeatureVariables(),
      item.event,
      handler);
  }

  updateCalculatedFields(cb) {
    this.updateAuditData();

    const callback = cb || (() => {
      this.updateView();
    });

    if (!this.state.editing) {
      return callback();
    }

    // If we're currently processing calculations and interacting with the expression engine,
    // don't attempt to use the engine since it's in the middle of executing the expressions.
    // Since even the most fundamental interactions with the engine are asynchronous through
    // the iframe, 'updating' calculation fields requires multiple coordinated async calls to
    // run 1 iteration of the engine. `prepare`, `setExpressions`, `setValues`, and `evaluate`
    // are all asynchronous. So we don't want to start preparing the engine while it's already
    // between setExpressions and setValues. The code below basically implements an exclusive
    // lock on the engine, along with a deferred queuing of updating once it's done. So if the
    // application attempts to update the calculations while they're being updated, we save the
    // callback into `_queueCalculationCallback` and return immediately. Once the current engine
    // is fully completed, we check for any attempts to update while it was being updated and
    // start a new attempt to update the calculations.
    if (this._processingCalculations) {
      this._queueCalculationCallback = callback;
      return null;
    }

    clearTimeout(this._calculationFieldTimeout);

    this._calculationFieldTimeout = setTimeout(() => {
      this._processingCalculations = true;

      this.updateCalculatedFieldsNow(() => {
        this._processingCalculations = false;

        /* eslint-disable callback-return */
        callback();
        /* eslint-enable callback-return */

        if (this._queueCalculationCallback) {
          const queued = this._queueCalculationCallback;

          this._queueCalculationCallback = null;

          this.updateCalculatedFields(queued);
        }
      });
    }, CALCULATION_FIELD_DELAY);

    return null;
  }

  updateCalculatedFieldsNow(cb) {
    const { valuesForConditions } = this.currentLevel;

    const engine = this.expressionEngine;

    // If there is no engine, it's unmounting, just exit
    // This happens rarely when data events contain SETINTERVAL scripts that can
    // cause all kinds of strange timing quirks when the handlers are invoked at
    // arbitrary times.
    if (!engine) {
      return;
    }

    const callback = cb || (() => {
      this.updateView();
    });

    const handler = (results) => {
      this.processExpressionResults(results, callback);
    };

    const calculationValues = valuesForConditions.copy();

    calculationValues.clearInvisibleValues(valuesForConditions, this.currentLevel.record);

    engine.evaluateExpressions(this.currentLevel.record.form,
      this.currentLevel.calculationExpressions,
      this.getFeatureVariables(),
      calculationValues,
      handler);
  }

  processExpressionResults(results, callback) {
    const validations = [];

    for (const result of results) {
      switch (result.type) {
        case 'calculation':
          this.processCalculationResult(result);
          break;

        case 'message':
          this.processMessageResult(result);
          break;

        case 'open':
          this.processOpenResult(result);
          break;

        case 'open-extension':
          this.processOpenExtensionResult(result);
          break;

        case 'set-value':
          this.processSetValueResult(result);
          break;

        case 'update-element':
          this.processUpdateElementResult(result);
          break;

        case 'progress':
          this.processProgressResult(result);
          break;

        case 'save':
          this.processSaveResult(result);
          break;

        case 'prevent-default':
          validations.push(new CustomValidationError());
          break;

        case 'validation':
          validations.push(this.processValidationResult(result));
          break;

        default:
          break;
      }
    }

    callback(validations);
  }

  processCalculationResult(result) {
    if (!result) {
      return;
    }

    const calculatedElement = this.currentLevel.record.form.elementsByKey[result.key];

    let calculatedValue = this.currentLevel.feature.formValues.get(calculatedElement.key);

    if (!calculatedValue) {
      calculatedValue = this.currentLevel.feature.formValues.createValue(calculatedElement, null);
    }

    // detect changes in the calculation field and only invoke the setter if it actually
    // is changed. This allows data events to use change scripts on calculation fields and
    // end up with sensible behavior.
    let oldValue = calculatedValue.textValue;
    let newValue = result.value != null ? result.value.toString() : null;

    oldValue = (!oldValue || oldValue.length === 0) ? null : oldValue;
    newValue = (!newValue || newValue.length === 0) ? null : newValue;

    const changed = (!oldValue && newValue)
      || (oldValue && !newValue)
      || (oldValue && newValue && oldValue !== newValue);

    calculatedValue.textValue = newValue;
    calculatedValue.error = result.error;

    if (changed) {
      this.setFormValue(calculatedValue.element, calculatedValue, null, () => {
        this.updateViewPrettySoon();
      });
    }
  }

  processOpenResult(result) {
    if (result.value) {
      window.open(result.value);
    }
  }

  processOpenExtensionResult(result) {
    if (result.value) {
      this.setState({
        showExtension: true,
        extensionParams: result.value,
      });
    }
  }

  processSetValueResult(result) {
    if (result.key === '@status') {
      this.updateStatusFromExpressionResult(result);
    } else if (result.key === '@project') {
      this.updateProjectFromExpressionResult(result);
    } else if (result.key === '@assignment') {
      this.updateAssignmentFromExpressionResult(result);
    } else if (result.key === '@geometry') {
      this.updateGeometryFromExpressionResult(result);
    } else {
      this.updateFieldFromExpressionResult(result);
    }
  }

  processMessageResult(result) {
    this.state.alertMessages.push(result);
    this.setState({ showAlert: true, alertMessages: this.state.alertMessages.slice(0) });
  }

  processProgressResult(result) {
    if (result.message != null) {
      this.setState({ showProgress: true, progressMessage: result.message });
    } else {
      this.setState({ showProgress: false });
    }
  }

  processSaveResult() {
    this.save({ validate: false, triggerSaveEvent: false });
  }

  processValidationResult(result) {
    return new CustomValidationError(result.message);
  }

  processUpdateElementResult(result) {
    let element = null;
    let valid = false;

    if (result.key === '@status') {
      valid = true;
      element = this.props.record.form.statusField;
    } else if (result.key === '@project') {
      valid = true;
      element = this.props.record.form.projectField;
    } else if (result.key === '@form') {
      valid = true;
    } else {
      element = this.props.record.form.elementsByKey[result.key];
      valid = (element != null);
    }

    if (!valid || !result.attribute) {
      return;
    }

    const { value } = result;

    const valueAsString = value ? value.toString() : null;
    const valueAsBoolean = value != null ? !!value : null;
    const valueAsNumber = value != null ? +value : null;
    const valueAsArray = Array.isArray(value) ? value : null;

    switch (result.attribute) {
      case 'label': {
        if (element) {
          element.overrideLabel = valueAsString;
        }
        break;
      }

      case 'description': {
        if (element) {
          element.overrideDescription = valueAsString;
        }
        break;
      }

      case 'required': {
        if (element) {
          element.overrideIsRequired = valueAsBoolean;
        }
        break;
      }

      case 'hidden': {
        if (element) {
          element.overrideIsHidden = valueAsBoolean;
        }
        break;
      }

      case 'disabled': {
        if (element) {
          element.overrideIsDisabled = valueAsBoolean;
        }
        break;
      }

      case 'min_length': {
        if (element) {
          element.overrideMinLength = valueAsNumber;
        }
        break;
      }

      case 'max_length': {
        if (element) {
          element.overrideMaxLength = valueAsNumber;
        }
        break;
      }

      case 'choices': {
        if (element) {
          if (element.isChoiceElement) {
            element.overrideChoices = valueAsArray;
          } else if (element.isClassificationElement) {
            element.overrideClassificationItems = valueAsArray;
          }
        }

        break;
      }

      case 'choice_filter': {
        if (element) {
          if (element.isChoiceElement) {
            element.choiceFilter = valueAsArray;
          } else if (element.isClassificationElement) {
            element.classificationFilter = valueAsArray;
          } else if (element.isStatusElement) {
            element.statusFilter = valueAsArray;
          }
        }

        break;
      }

      case 'media_gallery_enabled': {
        if (element instanceof MediaElement) {
          element.overrideMediaGalleryEnabled = valueAsBoolean;
        } else {
          this.props.record.form.overrideMediaGalleryEnabled = valueAsBoolean;
        }

        break;
      }

      case 'manual_location_enabled': {
        this.props.record.form.overrideManualLocationEnabled = valueAsBoolean;

        break;
      }

      case 'edit_durations_enabled': {
        this.props.record.form.overrideEditDurationsEnabled = valueAsBoolean;

        break;
      }

      default: break;
    }
  }

  updateStatusFromExpressionResult(result) {
    // TODO(zhm) validate status option or else it will 422
    if (this.context.role.canChangeStatus) {
      this.currentLevel.record.status = result.value;
    }
  }

  updateProjectFromExpressionResult(result) {
    const project = this.context.projects.find((p) => p.name === result.value);

    if (this.context.role.canChangeProject) {
      this.props.record.projectID = project ? project.id : null;
    }
  }

  updateAssignmentFromExpressionResult(result) {
    const user = this.context.users.find((u) => u.email === result.value);

    if (this.context.role.canAssignRecords) {
      this.props.record.assignedToID = user ? user.id : null;
    }
  }

  updateGeometryFromExpressionResult(result) {
    if (result.value?.coordinates) {
      this.updateGeometry(result.value);
    } else if (result.value == null) {
      this.updateGeometry(null);
    }
  }

  updateFieldFromExpressionResult(result) {
    const element = this.currentLevel.record.form.elementsByKey[result.key];

    // TODO(zhm) check that `element` is in the current scope (rootElement)
    // the expression engine has pretty solid validations to make sure that `element` is a valid element
    // to set on the form values. There still could be some kind of check when adding a value into the
    // form values that makes sure it's valid. The problem is that when we save values to the query table,
    // we do a values-first loop to build the columns. e.g. formValues.allValues.each ...

    if (element) {
      // escape this value here?
      const formValue = this.currentLevel.feature.formValues.createValue(element, result.value);

      if (formValue) {
        this.setFormValue(element, formValue, { silent: true });

        if (element.isRecordLinkElement && formValue && !formValue.isEmpty) {
          const item = formValue.items[0];

          item.load(this.context.dataSource, () => {
            if (item.record) {
              this.updateRecordLinkDefaults(formValue, item.record);
            }
          });
        }
      }
    }
  }

  updateGeometry = (geometry: Coordinate | GeometricShape | null) => {
    if (geometry) {
      if ('latitude' in geometry && 'longitude' in geometry) {
        this.currentLevel.feature.latitude = geometry.latitude;
        this.currentLevel.feature.longitude = geometry.longitude;
        this.currentLevel.feature.geometry = this.buildPoint(geometry);
      } else if ('type' in geometry && 'coordinates' in geometry) {
        this.currentLevel.feature.geometry = geometry;
        this.currentLevel.feature.latitude = null;
        this.currentLevel.feature.longitude = null;
      }
    } else {
      this.currentLevel.feature.latitude = null;
      this.currentLevel.feature.longitude = null;
      this.currentLevel.feature.geometry = null;
    }
  };

  buildPoint = (coordinate: Coordinate) => ({
    type: 'Point',
    coordinates: [
      coordinate.longitude,
      coordinate.latitude,
    ],
  });

  setFormValue(element, value, formValueOptions, callback) {
    this.currentLevel.feature.formValues.set(element.key, value);
    this.currentLevel.valuesForConditions.set(element.key, value);

    // Callers can optionally make it not trigger data event change events
    // and also skip calculation field updates. This is critical for performance
    // when we know we need to make multiple changes to the record at once. When
    // populating record link defaults, we want to write all the fields, trigger
    // the change events once, and then once we're totally done with the data
    // event events we update all the calculations.
    const options = formValueOptions || { silent: false, calculations: true };

    if (options.calculations == null) {
      options.calculations = true;
    }

    if (!options.silent) {
      // trigger the data event and update the calc fields in case data
      // changes in an event that is referenced in a calculation.
      this.triggerValueChange(element, value, () => {
        if (options.calculations) {
          return this.updateCalculatedFields(callback);
        }

        return callback();
      });
    } else if (callback) {
      return callback();
    }

    return null;
  }

  onProcessAsyncExpressionResults(results) {
    this.processExpressionResults(results, () => {
      this.updateCalculatedFields();
    });
  }

  onProcessExtensionMessage(message) {
    if (message.close === true) {
      this.setState({ showExtension: false });
    }

    const event = {
      name: 'extension-message',
      id: message.id,
      data: message.data,
      source: message.source,
    };

    this.triggerDataEvent(event, null);
  }

  get currentLevel() {
    return this.state.levels[this.state.levels.length - 1];
  }

  getFeatureVariables() {
    const variables = {
      userEmail: this.context.user.email,
      userFullName: this.context.user.fullName,
      userRoleName: this.context.role.name,
      recordID: this.currentLevel.record.id,
      recordStatus: this.currentLevel.record.status,
      recordClientCreatedAt: DateUtils.formatEpochTimestamp(this.currentLevel.record.clientCreatedAt),
      recordClientUpdatedAt: DateUtils.formatEpochTimestamp(this.currentLevel.record.clientUpdatedAt),
      recordProject: this.currentLevel.record.projectID,
      recordProjectName: this.recordProjectName,
      recordGeometry: this.currentLevel.record.geometryAsGeoJSON,
      recordAltitude: this.currentLevel.record.altitude,
      recordVerticalAccuracy: this.currentLevel.record.verticalAccuracy,
      recordHorizontalAccuracy: this.currentLevel.record.horizontalAccuracy,
      recordCreatedLatitude: this.currentLevel.record.createdLatitude,
      recordCreatedLongitude: this.currentLevel.record.createdLongitude,
      recordCreatedAltitude: this.currentLevel.record.createdAltitude,
      recordCreatedAccuracy: this.currentLevel.record.createdAccuracy,
      recordUpdatedLatitude: this.currentLevel.record.updatedLatitude,
      recordUpdatedLongitude: this.currentLevel.record.updatedLongitude,
      recordUpdatedAltitude: this.currentLevel.record.updatedAltitude,
      recordUpdatedAccuracy: this.currentLevel.record.updatedAccuracy,
      recordCreatedDuration: this.currentLevel.record.createdDuration,
      recordUpdatedDuration: this.currentLevel.record.updatedDuration,
      recordEditedDuration: this.currentLevel.record.editedDuration,
      repeatable: this.currentLevel.feature instanceof RepeatableItemValue ? this.currentLevel.feature.element.key : null,
      featureID: this.currentLevel.feature.id,
      featureIndex: this.currentLevel.feature instanceof RepeatableItemValue ? this.getFeatureIndex(this.currentLevel.feature) : null,
      featureIsNew: this.state.addMode,
      featureCreatedAt: DateUtils.formatEpochTimestamp(this.currentLevel.feature.createdAt),
      featureUpdatedAt: DateUtils.formatEpochTimestamp(this.currentLevel.feature.updatedAt),

      featureGeometry: this.currentLevel.feature.geometryAsGeoJSON,
      featureCreatedLatitude: this.currentLevel.feature.createdLatitude,
      featureCreatedLongitude: this.currentLevel.feature.createdLongitude,
      featureCreatedAltitude: this.currentLevel.feature.createdAltitude,
      featureCreatedAccuracy: this.currentLevel.feature.createdAccuracy,
      featureUpdatedLatitude: this.currentLevel.feature.updatedLatitude,
      featureUpdatedLongitude: this.currentLevel.feature.updatedLongitude,
      featureUpdatedAltitude: this.currentLevel.feature.updatedAltitude,
      featureUpdatedAccuracy: this.currentLevel.feature.updatedAccuracy,
      featureCreatedDuration: this.currentLevel.feature.createdDuration,
      featureUpdatedDuration: this.currentLevel.feature.updatedDuration,
      featureEditedDuration: this.currentLevel.feature.editedDuration,
      currentLocation: null,
    };

    return variables;
  }

  getFeatureIndex(feature) {
    const index = this.currentLevel
      .parentValues
      .all
      .filter((item) => item instanceof RepeatableValue)
      .map((x) => x.itemIndex(feature.id))
      .filter((i) => i >= 0)
      .flat()[0];

    const result = (typeof index === 'number') ? index : feature.index;

    return result;
  }

  updateDimensions() {
    this.setState({
      containerHeight: this.container.clientHeight,
    });
  }

  get validationErrors() {
    return FeatureValidator.validateFeature(this.currentLevel.feature,
      this.props.record,
      this.currentLevel.valuesForConditions);
  }

  get recordProjectName() {
    if (!this.currentLevel.record.projectID) {
      return null;
    }

    const project = this.context.projects.find((p) => p.id === this.currentLevel.record.projectID);

    return project ? project.name : null;
  }

  get isRootEditor() {
    // if the root element is null or it's a repeatable, we are the top level of the current
    // feature being edited
    return (this.currentLevel.rootElement == null
            || this.currentLevel.rootElement.isRepeatableElement);
  }

  get isRecordEditor() {
    return this.currentLevel.feature instanceof Record;
  }

  get isRepeatableEditor() {
    // returns true when the editor is editing a repeatable item (even inside nested sections)
    return this.currentLevel.feature instanceof RepeatableItemValue;
  }

  get isRootRecordEditor() {
    return this.isRootEditor && this.isRecordEditor;
  }

  get isRootRepeatableEditor() {
    return this.isRootEditor && this.isRepeatableEditor;
  }

  get hasChanges() {
    if (this.state.addMode) {
      return true;
    }

    // check for changes made inside the values
    const currentState = new FeatureEditState(this.currentLevel.feature);

    // clear the invisible values so the comparison is using the logically valid values.
    // this ensures conditionally visible fields with default values are cleared so the comparison for
    // changes doesn't include those hidden defaulted fields
    currentState.formValues.clearInvisibleValues(this.currentLevel.valuesForConditions,
      this.currentLevel.record);

    const hasFeatureChanges = !currentState.isEqual(this.state.originalFeatureState);

    return hasFeatureChanges;
  }

  get isEditingViaUrlAction() {
    return window.location.href.includes('mode=edit');
  }

  updateRecordLinkDefaults(recordLinkValue, record) {
    recordLinkValue.addRecord(record);

    DefaultValues.applyDefaultValuesForRecordLinkValue(this.context.dataSource,
      recordLinkValue,
      this.currentLevel.feature.formValues,
      this.currentLevel.record,
      () => {
        async.eachSeries(recordLinkValue.element.recordDefaults, (recordDefault, callback) => {
          const newElement = this.currentLevel.record.form.elementsByKey[recordDefault.destinationKey];

          if (!newElement) { return callback(); }

          const newValue = this.currentLevel.feature.formValues.get(newElement.key);

          if (newValue == null) { return callback(); }

          this.setFormValue(newElement, newValue, { silent: false, calculations: false }, callback);
        }, () => {
          // after all of the data event triggers are done, we update the calc fields and then
          // notify of the change
          this.updateCalculatedFields();
        });
      });
  }

  close() {
    // When closing the root editor, reset the override values so anything referencing the same form instance
    // gets the original form state without any changes that were made at runtime by data events during the
    // current edit session.
    if (this.currentLevel.isRecordLevel) {
      if (this.state.editing && this.state.addMode) {
        this.triggerEvent('cancel-record');
      }

      this.triggerEvent('unload-record');

      this.props.record.form.resetOverrides();
      this.context.preferences?.removeLastEditorMapExtents();
    }

    if (this.currentLevel.isRepeatableLevel) {
      if (this.state.editing && this.state.addMode) {
        this.triggerEvent('cancel-repeatable', null, this.currentLevel.feature.element.dataName);
      }

      this.triggerEvent('unload-repeatable', null, this.currentLevel.feature.element.dataName);
    }

    if (this.props.onClose) {
      this.props.onClose(this);
    }
  }

  saveFeature(triggerSaveEvent: boolean) {
    // Only change the edit state if it's not closing. If the editor is just about to close, don't
    // modify the edit state because it causes visual wierdness of the form changing for just a second
    // while it's closing.
    const newEditing = this.isRepeatableEditor ? this.state.editing : !this.state.editing;

    if (this.isRepeatableEditor) {
      // save the repeatable
      this.currentLevel.feature.updateTimestamps();
      this.currentLevel.feature.formValues.clearInvisibleValues(this.currentLevel.valuesForConditions,
        this.currentLevel.record);

      const performSaveRepeatable = (validations) => {
        if (validations.length > 0) {
          return;
        }

        this.savePreviousDefaultsToStorage();
        this.setState({ showValidationErrors: false, editing: newEditing, addMode: false });

        if (this.props.onSave) {
          this.props.onSave(this.currentLevel.feature);
        }

        this.close();
      };

      if (triggerSaveEvent) {
        this.triggerEvent('save-repeatable', null, this.currentLevel.feature.element.dataName, performSaveRepeatable);
      } else {
        performSaveRepeatable([]);
      }
    } else {
      // save the record
      this.currentLevel.feature.updateTimestamps();

      this.currentLevel.feature.formValues.clearInvisibleValues(this.currentLevel.valuesForConditions,
        this.currentLevel.record);

      const performSaveRecord = (validations) => {
        if (validations.length > 0) {
          return;
        }

        this.context.preferences?.removeLastEditorMapExtents();

        this.savePreviousDefaultsToStorage();
        this.setState({ loading: true });
        this.saveRecord();
      };

      if (triggerSaveEvent) {
        this.triggerEvent('save-record', null, null, performSaveRecord);
      } else {
        performSaveRecord([]);
      }
    }
  }

  assignRecordIfNecessary() {
    const { form } = this.props.record;

    if (this.props.addMode && form.isAssignmentEnabled && form.isAutoAssign && !this.props.record.assignedToID) {
      this.props.record.assignedToID = this.context.user.id;
    }
  }

  applyDefaultValuesIfNecessary(editing) {
    if (!editing || this._defaultsApplied) {
      return;
    }

    const defaultValues = this.loadPreviousDefaultsFromStorage();

    DefaultValues.applyPreviousDefaults(this.context.dataSource,
      defaultValues,
      this.props.feature.formValues,
      this.props.record,
      () => {});

    DefaultValues.applyDefaultValuesForElements(this.props.feature.formValues.elements,
      this.props.feature.formValues,
      this.props.record);

    this._defaultsApplied = true;
  }

  loadPreviousDefaultsFromStorage() {
    return localStorage.get(`default-values:${this.props.record.form.id}`, {});
  }

  savePreviousDefaultsToStorage() {
    const defaultValues = this.updatePreviousDefaults(this.currentLevel.feature.formValues, this.props.record);

    localStorage.set(`default-values:${this.props.record.form.id}`, defaultValues);
  }

  updatePreviousDefaults(formValues, record) {
    const values = this.loadPreviousDefaultsFromStorage();

    const elements = DefaultValues.elementsWithPreviousDefaultsEnabledWithinElements(formValues.elements,
      record.form);

    for (const element of elements) {
      const value = record.get(element.key, formValues);

      if (value && !value.isEmpty) {
        values[element.key] = value.toJSON();
      } else {
        delete values[element.key];
      }
    }

    return values;
  }

  resetEditStartTime() {
    this._editStartTime = new Date().getTime();
  }

  updateAuditData() {
    if (!this.state.editing || !this.props.feature) {
      return;
    }

    if (!this._editStartTime) {
      this.resetEditStartTime();
    }

    if (this.props.record.form.isEditDurationsEnabled) {
      if (this.state.addMode && (!this.state.originalFeatureState.createdDuration || !this.props.feature.createdDuration)) {
        this.props.feature.createdDuration = this.editDuration;
      }

      this.props.feature.updatedDuration = this.editDuration;

      if (this.state.originalFeatureState.editedDuration) {
        this.props.feature.editedDuration = this.state.originalFeatureState.editedDuration + this.editDuration;
      } else {
        this.props.feature.editedDuration = this.editDuration;
      }
    } else {
      this.props.feature.createdDuration = this.state.originalFeatureState.createdDuration;
      this.props.feature.updatedDuration = this.state.originalFeatureState.updatedDuration;
      this.props.feature.editedDuration = this.state.originalFeatureState.editedDuration;
    }
  }

  get editDuration() {
    if (this._editStartTime) {
      return Math.ceil((new Date().getTime() - this._editStartTime) / 1000);
    }

    return null;
  }
}

const styles = cssInJS({
  container: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    transition: 'opacity 160ms ease, left 160ms ease, marginLeft 160ms ease, width 160ms ease',
  },

  contentContainer: {
    display: 'flex',
    height: '100%',
    background: '#fff',
  },

  mapContainer: {
    zIndex: 3,
    flex: 'auto',
    borderRight: '1px solid #ddd',
    transition: 'all 160ms ease',
  },

  header: {
    height: 48,
  },

  controller: {
    top: 84,
    bottom: 0,
    left: 0,
    right: 0,
  },

  navigationContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: 700,
  },

  navigationBar: {
    height: 36,
  },

  editToolbar: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
    height: 50,
    zIndex: 1,
  },
});

Editor.styles = styles;
